import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import {
  EMPTY_ODDS_FEED,
  normalizeOddsFeedError,
} from "../utils/oddsCustomizeHelpers";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiErr = (error, fallback) =>
  error.response?.data?.message ||
  error.response?.data?.errors?.[0] ||
  fallback;

export const fetchFixtureOddsFeeds = createAsyncThunk(
  "oddsCustomize/fetchFixtureOddsFeeds",
  async ({ fixtureId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/odds/fixture/${fixtureId}`);
      return { fixtureId, data: res.data };
    } catch (error) {
      const message = normalizeOddsFeedError(error, "Failed to load odds feeds");
      if (!message) {
        return { fixtureId, data: EMPTY_ODDS_FEED };
      }
      return rejectWithValue(message);
    }
  }
);

export const fetchOddsAutofill = createAsyncThunk(
  "oddsCustomize/fetchOddsAutofill",
  async ({ fixtureId, bookmakerId, section }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/odds/fixture/${fixtureId}/autofill`, {
        bookmakerId,
        section,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiErr(error, "Failed to autofill odds"));
    }
  }
);

export const saveHouseFixtureOdds = createAsyncThunk(
  "oddsCustomize/saveHouseFixtureOdds",
  async ({ betId, fixtureId, odds, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/odds/${betId}/${fixtureId}`, {
        odds,
        userId: operatorUserId,
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(apiErr(error, "Failed to save odds"));
    }
  }
);

const oddsCustomizeSlice = createSlice({
  name: "oddsCustomize",
  initialState: {
    fixtureOddsFeeds: {},
    fixtureOddsFeedsStatus: "idle",
    fixtureOddsFeedsError: null,
    autofillResult: null,
    autofillStatus: "idle",
    autofillError: null,
    saveStatus: "idle",
    saveError: null,
  },
  reducers: {
    clearOddsAutofillResult: (state) => {
      state.autofillResult = null;
      state.autofillStatus = "idle";
      state.autofillError = null;
    },
    clearOddsSaveState: (state) => {
      state.saveStatus = "idle";
      state.saveError = null;
    },
    clearFixtureOddsFeeds: (state) => {
      state.fixtureOddsFeeds = {};
      state.fixtureOddsFeedsStatus = "idle";
      state.fixtureOddsFeedsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFixtureOddsFeeds.pending, (state) => {
        state.fixtureOddsFeedsStatus = "loading";
        state.fixtureOddsFeedsError = null;
      })
      .addCase(fetchFixtureOddsFeeds.fulfilled, (state, action) => {
        state.fixtureOddsFeedsStatus = "succeeded";
        const { fixtureId, data } = action.payload;
        state.fixtureOddsFeeds[fixtureId] = data;
      })
      .addCase(fetchFixtureOddsFeeds.rejected, (state, action) => {
        state.fixtureOddsFeedsStatus = "failed";
        state.fixtureOddsFeedsError = action.payload;
      })
      .addCase(fetchOddsAutofill.pending, (state) => {
        state.autofillStatus = "loading";
        state.autofillError = null;
      })
      .addCase(fetchOddsAutofill.fulfilled, (state, action) => {
        state.autofillStatus = "succeeded";
        state.autofillResult = action.payload;
      })
      .addCase(fetchOddsAutofill.rejected, (state, action) => {
        state.autofillStatus = "failed";
        state.autofillError = action.payload;
      })
      .addCase(saveHouseFixtureOdds.pending, (state) => {
        state.saveStatus = "loading";
        state.saveError = null;
      })
      .addCase(saveHouseFixtureOdds.fulfilled, (state) => {
        state.saveStatus = "succeeded";
      })
      .addCase(saveHouseFixtureOdds.rejected, (state, action) => {
        state.saveStatus = "failed";
        state.saveError = action.payload;
      });
  },
});

export const {
  clearOddsAutofillResult,
  clearOddsSaveState,
  clearFixtureOddsFeeds,
} = oddsCustomizeSlice.actions;

export default oddsCustomizeSlice.reducer;
